import React, { useState } from 'react';

import { Box, Button, Checkbox, Divider, FormControlLabel, Stack, Switch } from '@mui/material';
import { TextField } from '@mui/material';

import ReservationService from '~/services/ReservationService';

import { formatValidationMessages } from '~/utils/formatValidationMessages';

import ErrorListDisplay from '../ErrorListDisplay';

import { buttonMessages, buttonStates } from './states/submitButton';

interface Props {
  onCancel: () => void;
  onRoomTypeIncludedGuestUpdated: (result: any) => void;
  propertyId: string;
  roomRate: any;
  roomType: any;
  includedGuest?: any;
}

interface FormState {
  maxIncludedGuests: number;
  maxAdultIncludedGuests: number;
  maxChildIncludedGuests: number;
  maxInfantIncludedGuests: number;
  maxTeenagerIncludedGuests: number;
  includeInfants: boolean;
}

export default function RoomTypeIncludedGuestsForm({
  onCancel,
  onRoomTypeIncludedGuestUpdated,
  propertyId,
  roomRate,
  roomType,
}: Props) {
  const [formState, setFormState] = useState<FormState>({
    maxIncludedGuests: roomType.max_included_guests,
    maxAdultIncludedGuests: roomType.max_adult_included_guests,
    maxChildIncludedGuests: roomType.max_child_included_guests,
    maxInfantIncludedGuests: roomType.max_infant_included_guests,
    maxTeenagerIncludedGuests: roomType.max_teenager_included_guests,
    includeInfants: false,
  });

  const [saveState, setSaveState] = useState(buttonStates.default);
  const [errors, setErrors] = useState(undefined);
  const [showAsGuests, setShowAsGuests] = useState(false);

  const handleValueChange = (fieldName: keyof FormState, value: string | boolean) => {
    setFormState((prev) => ({
      ...prev,
      [fieldName]: typeof value === 'boolean' ? value : parseInt(value) || 0,
    }));
  };

  const handleSubmit = async () => {
    if (!window.confirm('this will regenerate all included guests, are you sure?')) {
      return;
    }

    setSaveState(buttonStates.saving);
    setErrors(null);

    const data = {
      max_included_guests: formState.maxIncludedGuests,
      max_adult_included_guests: formState.maxAdultIncludedGuests,
      max_child_included_guests: formState.maxChildIncludedGuests,
      max_infant_included_guests: formState.maxInfantIncludedGuests,
      max_teenager_included_guests: formState.maxTeenagerIncludedGuests,
      include_infants: formState.includeInfants,
    };

    try {
      setSaveState(buttonStates.saved);
      setErrors(null);

      const response = await ReservationService.updateRoomTypeIncludedGuests(
        data,
        propertyId,
        roomType.id,
        roomRate.id,
      );

      setSaveState(buttonStates.saved);
      setErrors(null);
      onRoomTypeIncludedGuestUpdated(response.result);
    } catch (error) {
      const validationErrors = error.name === 'ValidationError' ? error.errors : null;
      setSaveState(buttonStates.failed);
      setErrors(validationErrors);
      reportError(error);
    }
  };

  const formRow = (label: string, fieldName: keyof FormState) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Box sx={{ flex: 1 }}>{label}</Box>
      <Box sx={{ flex: 1 }}>
        <TextField
          type="number"
          inputProps={{ min: 0, step: 1 }}
          value={formState[fieldName]}
          onChange={(e) => handleValueChange(fieldName, e.target.value)}
          fullWidth
          size="small"
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: 1 }}>
      {formRow('Total number of guests', 'maxIncludedGuests')}

      <Divider sx={{ my: 2 }} />

      <FormControlLabel
        control={<Switch checked={showAsGuests} onChange={(e) => setShowAsGuests(e.target.checked)} size="small" />}
        label="Display 'Adults' as 'Guests'"
        sx={{ mb: 2 }}
      />

      {formRow(showAsGuests ? 'Guests' : 'Adults', 'maxAdultIncludedGuests')}
      {formRow('Children', 'maxChildIncludedGuests')}
      {formRow('Infants', 'maxInfantIncludedGuests')}
      {formRow('Teenagers', 'maxTeenagerIncludedGuests')}

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ flex: 1 }}>Include infants</Box>
        <Box sx={{ flex: 1 }}>
          <Checkbox
            checked={formState.includeInfants}
            onChange={(e) => handleValueChange('includeInfants', e.target.checked)}
            size="small"
          />
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />
      {errors && (
        <Box sx={{ mb: 2 }}>
          <ErrorListDisplay messages={formatValidationMessages(errors)} />
        </Box>
      )}

      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" type="submit" disabled={saveState === buttonStates.saving} onClick={handleSubmit}>
          {buttonMessages[saveState]}
        </Button>
      </Stack>
    </Box>
  );
}
